/**
 * "Settled" — the SPA's answer to the EJS page's navigation.
 *
 * The EJS pages POST a form and navigate, so a test that clicks Save can wait for the load event and be sure
 * the write landed. The SPA fetches and re-renders: `load` is long over, and an assertion right after the click
 * reads the DOM from BEFORE the write. There is no navigation to wait for, so the page has to say when it is
 * done — that is what this module provides, and it is deliberately observable rather than a timeout:
 *
 *   document.documentElement.dataset.pfBusy   "1" while a fetch or a queued render is in flight
 *   document.documentElement.dataset.pfReady  "1" once the first paint with no fetch in flight has happened
 *
 * The test helper (test/ui/fixtures.js `settled`) waits for pfReady and then for pfBusy to clear twice across
 * two animation frames, which is "the fetch finished and React has painted its result". Both are attributes on
 * <html>, so nothing about the app's own behaviour depends on them.
 */
const w = window as unknown as { fetch: typeof fetch };

let inflight = 0;
let queued = false;

function paint() {
  if (queued) return;
  queued = true;
  // Two frames: the first lets React commit, the second guarantees the browser has painted the commit. One
  // frame is enough in practice and not in principle, and this is the kind of thing that fails on a busy box.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      queued = false;
      if (inflight === 0) {
        document.documentElement.dataset.pfReady = '1';
        delete document.documentElement.dataset.pfBusy;
      }
    });
  });
}

function track(delta: number) {
  inflight += delta;
  if (inflight > 0) {
    document.documentElement.dataset.pfBusy = '1';
  } else {
    paint();
  }
}

/**
 * Declare the first paint. The ROOT component's effect calls this — never module load.
 *
 * A page's data fetch starts in a mount effect, and React runs a child's effect before its parent's. Called from
 * module load, `paint()` could therefore see no fetch in flight and mark the app ready while the fetch that fills
 * the page was still a tick away: `settled()` returned, the assertion read a half-painted page, and the same test
 * passed on a quiet box and failed on a busy one. From the root's effect, every mount-time fetch is already
 * counted, so readiness waits for it.
 */
export function markReady(): void {
  paint();
}

if (!(w as unknown as { __pfTracked?: boolean }).__pfTracked) {
  (w as unknown as { __pfTracked?: boolean }).__pfTracked = true;
  const real = w.fetch.bind(window);
  w.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    track(1);
    return real(input, init).finally(() => track(-1));
  }) as typeof fetch;
}
